import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, getCurrentInstance } from 'vue';
import type { CreateElement } from 'vue';

import { FilterProductionCountry, FilterRuntime, FilterVoteCount } from '@/lazy-load-components';

// sub-components specific application modules
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';
import FilterAgeCertification from '@/components/filter-bar/FilterAgeCertification.vue';
import FilterBarGenres from '@/components/filter-bar/FilterBarGenres.vue';
import FilterButtonSeo from '@/components/filter-bar/seo/FilterButtonSeo.vue';
import FilterMonetizationTypes from '@/components/filter-bar/FilterMonetizationTypes.vue';
import FilterPresentationTypes from '@/components/filter-bar/FilterPresentationTypes.vue';
import FilterPrice from '@/components/filter-bar/FilterPrice.vue';
import FilterRating from '@/components/filter-bar/FilterRating.vue';
import FilterReleaseYear from '@/components/filter-bar/FilterReleaseYear.vue';
import ChipButton from '@/ui-components/ChipButton.vue';

import { CollectionType } from '@/enums/collection-type';

import { FilterValues, getUsedFilters, isAnyFilterActive } from '@/helpers/filter-helper';
import { showProFeatures } from '@/helpers/pro-web-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { FilterCollection } from '@/stores/filter.store';

import { useConstantStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import type Provider from '@/interfaces/provider';
import { faChevronDown, faChevronUp, faFilter } from '@fortawesome/free-solid-svg-icons';

type FilterType<T> = { name: string; filters: T };

interface AdditionalFilters {
	releaseYear: FilterType<[typeof FilterReleaseYear]>;
	genres: FilterType<[typeof FilterBarGenres]>;
	price: FilterType<[typeof FilterPrice, typeof FilterMonetizationTypes, typeof FilterPresentationTypes]>;
	rating: FilterType<[typeof FilterRating, typeof FilterVoteCount?]>;
	prodCountries?: FilterType<[typeof FilterProductionCountry]>;
	runTime?: FilterType<[typeof FilterRuntime]>;
	ageRating?: FilterType<[typeof FilterAgeCertification]>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarSeo',
  props: {
    collectionType: null,
    currentFilters: null,
    newPageType: null,
    onlyShowFilters: { type: Boolean, default: false },
    isProviderPage: { type: Boolean, default: false },
    isFiltersVisible: { type: Boolean, default: true },
    popularPageExpActive: { type: Boolean, default: false }
  },
  emits: ['click', 'resetClicked'],
  setup(__props: any) {

const props = __props as { collectionType: CollectionType, currentFilters: FilterCollection, newPageType?: CollectionType | undefined, onlyShowFilters: boolean, isProviderPage: boolean, isFiltersVisible: boolean, popularPageExpActive: boolean };

const FilterBarContentTypeSeo = () => import('@/components/filter-bar/seo/FilterBarContentTypeSeo.vue');

const FilterBarProviderSeo = () => import('@/components/filter-bar/seo/FilterBarProviderSeo.vue');





const { ageCertifications, providers } = useConstantStore();
const { webLocale } = useLanguageStore();
const { isPremium, isLoggedIn } = useUserStore();

const { tc } = useI18n();

/**
 * helper to determine the affected filter keys.
 * omit filterComponent to get all possible affected filter keys.
 */
const additionalFilters = computed(() => {
	const baseFilters: AdditionalFilters = {
		releaseYear: { name: tc('WEBAPP_RELEASEYEAR'), filters: [FilterReleaseYear] },
		genres: { name: tc('WEBAPP_GENRES'), filters: [FilterBarGenres] },
		price: {
			name: tc('WEBAPP_PRICE'),
			filters: [FilterPrice, FilterMonetizationTypes, FilterPresentationTypes],
		},
		rating: { name: tc('WEBAPP_RATING'), filters: [FilterRating] },
	};

	if (showProFeatures(webLocale.value)) {
		baseFilters.prodCountries = {
			name: tc('WEBAPP_TITLE_DETAIL_PROD_COUNTRIES'),
			filters: [FilterProductionCountry],
		};
		baseFilters.runTime = { name: tc('WEBAPP_RUNTIME'), filters: [FilterRuntime] };

		// if premium user - add imdb popularity filter to the popover
		baseFilters.rating.filters = [FilterRating, FilterVoteCount];
	}

	// decide if the age rating should be added to all additional filters
	if (ageCertifications.value.hasCertifications) {
		baseFilters.ageRating = { name: tc('WEBAPP_AGERATING'), filters: [FilterAgeCertification] };
	}

	return baseFilters;
});

const filterBarSelectionType = computed(() => (isPopularPage.value && !isLoggedIn.value ? 'bundle' : 'single'));

const filterValues = computed(() => {
	const { FilterProviders, ...filterValues } = FilterValues;
	if (!ageCertifications.value.hasCertifications) {
		delete filterValues['FilterAgeCertification'];
	}

	return filterValues;
});

const isSearchTab = computed(() => props.collectionType === CollectionType.SEARCH);
const isPopularPage = computed(() => props.collectionType === CollectionType.POPULAR);

// filtering out Justwatch-plex Provider as it's only used by partners.
const userProviders = computed(() => providers.value().filter(({ shortName }: Provider) => shortName !== 'plc'));

// we only calculate the active filters, when the currentFilters have changed.
// otherwise we'll use up computation power, especially during navigation which affects INP.
const activeFilters = ref<Record<string, boolean>>({});
watch(
	() => props.currentFilters,
	() => {
		activeFilters.value = Object.keys(additionalFilters.value).reduce((root, key) => {
			const item = additionalFilters.value[key as keyof AdditionalFilters];
			root[key] = isAnyFilterActive(getUsedFilters(filterValues.value, item?.filters), props.currentFilters);
			return root;
		}, {} as Record<string, boolean>);
	},
	{ immediate: true }
);

const instance = getCurrentInstance();
const openedPopoverKey = ref<keyof AdditionalFilters | null>(null);
async function openPopover(additionalFilterKey: keyof AdditionalFilters, event: Event) {
	openedPopoverKey.value = additionalFilterKey;

	const { popoverController } = await import('@/ionic.loader');
	const modal = await popoverController.create({
		cssClass: 'popover__filter',
		component: {
			render: (h: CreateElement) => {
				const addFilters = additionalFilters.value[additionalFilterKey];

				if (addFilters?.filters == null) {
					return h('div', [h('div', { style: { padding: '10px' } }, 'N/A no filters yet')]);
				}

				return h(
					'div',
					addFilters.filters.map(item =>
						h(item, {
							props: {
								collectionType: props.collectionType,
								currentFilters: props.currentFilters,
							},
						})
					)
				);
			},
		},
		componentProps: { parent: instance?.proxy },
		event,
		mode: 'ios',
	} as any);
	modal.present();

	const contexts = [];
	if (isPremium.value) {
		const { getFreemiumContext } = await import('@/helpers/iap-helper');
		contexts.push(getFreemiumContext());
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_popover_toggled',
			label: additionalFilterKey,
		},
		contexts
	);

	await modal.onWillDismiss();
	openedPopoverKey.value = null;
}

return { __sfc: true,FilterBarContentTypeSeo, FilterBarProviderSeo, props, ageCertifications, providers, webLocale, isPremium, isLoggedIn, tc, additionalFilters, filterBarSelectionType, filterValues, isSearchTab, isPopularPage, userProviders, activeFilters, instance, openedPopoverKey, openPopover, HiddenHorizontalScrollbar, ResetButton, FilterButtonSeo, ChipButton, faChevronDown, faChevronUp, faFilter }
}

})